import { getLocalTimeZone } from '@solinkcorp/utils';
import { usePlaylistCache } from 'components/CacheContext';
import { useEffect } from 'react';
import { useLocalController } from 'solox';
import { PlaybackController } from './PlaybackController';
import { type PlayingCamera } from './PlayingCamerasController';
import { usePauseOnWindowHidden } from './usePauseOnWindowHidden';
import { usePlaybackControllerTick } from './usePlaybackControllerTick';

function usePublicPlaybackController(
  playingCameras: PlayingCamera[],
  initialMasterTime: number | (() => number),
  options: { vtt?: boolean } = {},
  videoPlaybackStartsAtDate: Date | undefined = undefined,
  videoPlaybackEndsAtTime: Date | undefined = undefined,
  timezone = getLocalTimeZone(),
): PlaybackController {
  const playlistCache = usePlaylistCache();

  const playbackController = useLocalController(() => {
    const masterTime =
      typeof initialMasterTime === 'function' ? initialMasterTime() : initialMasterTime;

    return new PlaybackController(
      undefined,
      playlistCache,
      undefined,
      'localonly',
      masterTime,
      // publicly shared clip and alarm
      'hd',
      playingCameras,
      {},
      options,
      videoPlaybackStartsAtDate,
      videoPlaybackEndsAtTime,
      timezone,
    );
  });

  useEffect(() => {
    playbackController.setPlayingCameras(playingCameras);
  }, [playbackController, playingCameras]);

  usePlaybackControllerTick(playbackController);

  usePauseOnWindowHidden(playbackController);

  useEffect(() => {
    return () => playbackController.destroyPressureStateTracker();
  }, [playbackController]);

  return playbackController;
}

export default usePublicPlaybackController;
